import { useState } from "react";
import { ChevronDown, Copy, FileDown, LoaderCircle, Printer, ShieldCheck, ThumbsDown, ThumbsUp } from "lucide-react";
import { SourceAnchor } from "@/components/source-anchor";
import { Badge } from "@shared/components/ui/badge";
import { Button } from "@shared/components/ui/button";
import { Card } from "@shared/components/ui/card";
import { useT } from "@shared/client/locale";
import type { Messages } from "@shared/i18n";
import type { MarketReport } from "@shared/lib/report";
import { cn } from "@shared/lib/utils";

function scoreLabel(score: number, labels: Messages["reports"]["report"]["score"]) {
  if (score >= 80) return labels.strong;
  if (score >= 65) return labels.promising;
  return labels.toConfirm;
}

/** Libellé de bloc éditorial : discret, en casse de phrase (charte v2). */
function Eyebrow({ className, children }: { className?: string; children: React.ReactNode }) {
  return (
    <span className={cn("text-[12px] font-medium", className)}>{children}</span>
  );
}

function OpportunityFeedback({ runId, organization }: { runId: string; organization: string }) {
  const copy = useT().reports.view.feedback;
  const [value, setValue] = useState<1 | -1 | null>(null);
  const [saving, setSaving] = useState(false);

  async function send(next: 1 | -1) {
    const target = value === next ? null : next;
    setSaving(true);
    try {
      const response = await fetch("/api/feedback", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ runId, organization, value: target }),
      });
      if (response.ok) setValue(target as 1 | -1 | null);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div aria-label={copy.label(organization)} className="flex items-center gap-1.5">
      <span className="text-[12px] text-muted-foreground">{copy.prompt}</span>
      <Button
        aria-label={copy.relevant}
        className={cn(value === 1 && "border-success/40 text-success")}
        disabled={saving}
        onClick={() => send(1)}
        size="icon-sm"
        type="button"
        variant="outline"
      >
        <ThumbsUp size={14} />
      </Button>
      <Button
        aria-label={copy.offTarget}
        className={cn(value === -1 && "border-destructive/40 text-destructive")}
        disabled={saving}
        onClick={() => send(-1)}
        size="icon-sm"
        type="button"
        variant="outline"
      >
        <ThumbsDown size={14} />
      </Button>
    </div>
  );
}

/**
 * Export PDF : téléchargé par fetch plutôt que par un lien `download`, pour
 * qu'une erreur du serveur s'affiche au lieu d'être enregistrée comme fichier
 * (le navigateur gardait la réponse JSON sous le nom « pdf.json »).
 */
function PdfExportButton({ runId, className }: { runId: string; className?: string }) {
  const copy = useT().reports.view;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function exportPdf() {
    setBusy(true);
    setError("");
    try {
      const response = await fetch(`/api/reports/${runId}/pdf`);
      if (!response.ok || !response.headers.get("content-type")?.includes("application/pdf")) {
        const body = (await response.json().catch(() => null)) as { error?: string } | null;
        throw new Error(body?.error || copy.exportFailedRetry);
      }
      const filename =
        /filename="([^"]+)"/.exec(response.headers.get("content-disposition") ?? "")?.[1] ?? copy.defaultFilename;
      const url = URL.createObjectURL(await response.blob());
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : copy.exportFailed);
    } finally {
      setBusy(false);
    }
  }

  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      <Button className={className} disabled={busy} onClick={exportPdf} size="sm" type="button" variant="outline">
        {busy ? <LoaderCircle className="animate-spin" size={16} /> : <FileDown size={16} />} {copy.exportPdf}
      </Button>
      {error && (
        <span className="text-[12.5px] text-background/80" role="alert">
          {error}
        </span>
      )}
    </span>
  );
}

export function ReportView({ report, runId = null }: { report: MarketReport; runId?: string | null }) {
  const t = useT();
  const labels = t.reports.report;
  const actions = t.reports.view;
  const average = report.opportunities.length
    ? Math.round(
        report.opportunities.reduce((sum, opportunity) => sum + opportunity.priority, 0) /
          report.opportunities.length,
      )
    : 0;

  return (
    <section className="mt-12 mb-8 scroll-mt-8" id="report">
      <header className="grid items-end gap-7 rounded-t-[22px] bg-foreground p-10 text-background md:grid-cols-[1fr_auto] max-sm:rounded-t-2xl max-sm:p-6">
        <div>
          <Eyebrow className="flex items-center gap-2.5 text-success-muted">
            <span className="size-2 rounded-full bg-success-muted" /> {labels.done}
          </Eyebrow>
          <h2 className="mt-4 mb-3 font-serif text-[clamp(38px,6vw,66px)] leading-[0.95] font-normal tracking-[-0.04em]">
            {report.offer_name}
          </h2>
          <p className="m-0 max-w-[780px] leading-relaxed text-background/75">{report.executive_summary}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            className="border-background/25 bg-transparent text-background/90 hover:bg-background/10 hover:text-background"
            onClick={() => navigator.clipboard.writeText(window.location.href)}
            size="sm"
            type="button"
            variant="outline"
          >
            <Copy size={16} /> {actions.copyLink}
          </Button>
          {runId && (
            <PdfExportButton
              className="border-background/25 bg-transparent text-background/90 hover:bg-background/10 hover:text-background"
              runId={runId}
            />
          )}
          <Button
            className="border-background/25 bg-transparent text-background/90 hover:bg-background/10 hover:text-background"
            onClick={() => window.print()}
            size="sm"
            type="button"
            variant="outline"
          >
            <Printer size={16} /> {actions.print}
          </Button>
        </div>
      </header>

      <div className="grid border border-t-0 border-foreground bg-card md:grid-cols-[0.5fr_0.5fr_2fr]">
        <div className="flex min-h-[135px] flex-col justify-between max-sm:min-h-0 max-sm:gap-3 max-sm:p-5 p-6">
          <Eyebrow className="text-muted-foreground">{labels.opportunities}</Eyebrow>
          <strong className="font-serif text-[50px] leading-none font-normal">{report.opportunities.length}</strong>
        </div>
        <div className="flex min-h-[135px] flex-col justify-between max-sm:min-h-0 max-sm:gap-3 max-sm:p-5 border-t p-6 md:border-t-0 md:border-l">
          <Eyebrow className="text-muted-foreground">{labels.averagePriority}</Eyebrow>
          <strong className="font-serif text-[50px] leading-none font-normal">
            {average}
            <small className="ml-1 font-sans text-[14px] text-muted-foreground">/100</small>
          </strong>
        </div>
        <div className="flex min-h-[135px] flex-col justify-between max-sm:min-h-0 max-sm:gap-3 max-sm:p-5 border-t p-6 md:border-t-0 md:border-l">
          <Eyebrow className="text-muted-foreground">{labels.prioritySegment}</Eyebrow>
          <p className="m-0 max-w-[650px] text-[17px] leading-tight font-semibold">{report.priority_segment}</p>
        </div>
      </div>

      <Card className="mt-4.5 grid gap-0 overflow-hidden py-0 lg:grid-cols-[0.65fr_1.35fr]">
        <div className="border-b p-7 lg:border-r lg:border-b-0 max-sm:p-5">
          <Eyebrow className="text-destructive">{labels.strategy}</Eyebrow>
          <h3 className="mt-5 mb-3 font-serif text-[34px] leading-none font-normal max-sm:text-[28px]">{labels.strategyTitle}</h3>
          <p className="m-0 text-[13px] leading-relaxed text-muted-foreground">
            {report.research_strategy.offer_hypothesis}
          </p>
        </div>
        <div className="grid sm:grid-cols-2">
          {report.research_strategy.selected_signals.map((signal) => (
            <article className="border-b p-5 sm:odd:border-r" key={signal.family}>
              <h4 className="m-0 mb-2 text-[14px] font-semibold">{signal.family}</h4>
              <p className="m-0 mb-3 text-[12px] leading-relaxed text-muted-foreground">{signal.rationale}</p>
              <span className="text-[12px] text-muted-foreground">{signal.explored_sources.join(" · ")}</span>
            </article>
          ))}
          <p className="col-span-full m-0 bg-muted/50 px-5 py-4 text-[12px] leading-relaxed text-muted-foreground">
            {report.research_strategy.adaptation_note}
          </p>
        </div>
      </Card>

      <Card className="mt-4.5 grid gap-0 overflow-hidden border-foreground bg-accent py-0 lg:grid-cols-[0.65fr_1.35fr]">
        <div className="border-b border-foreground/25 p-7 lg:border-r lg:border-b-0 max-sm:p-5">
          <Eyebrow>{labels.target}</Eyebrow>
          <h3 className="mt-9 mb-0 max-w-[280px] font-serif text-[38px] leading-none font-normal max-sm:mt-5 max-sm:text-[30px]">
            {labels.targetTitle}
          </h3>
        </div>
        <dl className="m-0 grid sm:grid-cols-3">
          <div className="p-6 max-sm:p-5">
            <dt className="mb-5 text-[12px] font-medium">{labels.decisionMaker}</dt>
            <dd className="m-0 text-[14px] leading-relaxed">{report.audience.decision_maker}</dd>
          </div>
          <div className="border-t border-foreground/15 p-6 sm:border-t-0 sm:border-l max-sm:p-5">
            <dt className="mb-5 text-[12px] font-medium">{labels.desiredProgress}</dt>
            <dd className="m-0 text-[14px] leading-relaxed">{report.audience.desired_progress}</dd>
          </div>
          <div className="border-t border-foreground/15 p-6 sm:border-t-0 sm:border-l max-sm:p-5">
            <dt className="mb-5 text-[12px] font-medium">{labels.favorableEvent}</dt>
            <dd className="m-0 text-[14px] leading-relaxed">{report.audience.favorable_event}</dd>
          </div>
        </dl>
      </Card>

      <div className="mt-20 max-sm:mt-12">
        <div className="flex flex-wrap items-end justify-between gap-5 border-b border-foreground pb-4.5 max-sm:gap-3">
          <Eyebrow>{labels.priorities}</Eyebrow>
          <h3 className="m-0 max-w-[650px] text-right font-serif max-sm:text-left text-[clamp(30px,5vw,56px)] leading-[0.95] font-normal tracking-[-0.04em]">
            {labels.prioritiesTitle}
          </h3>
        </div>

        <div className="grid gap-4.5 pt-6">
          {report.opportunities.map((opportunity, index) => (
            <Card className="grid gap-0 overflow-hidden py-0 md:grid-cols-[84px_1fr]" key={`${opportunity.organization}-${index}`}>
              <div className="hidden place-items-center border-r bg-muted/40 font-serif text-[28px] text-muted-foreground md:grid">
                {String(index + 1).padStart(2, "0")}
              </div>
              <div className="grid gap-4.5 p-6 max-sm:p-5">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <Eyebrow className="text-muted-foreground">{opportunity.category}</Eyebrow>
                    <h4 className="mt-2 mb-0 font-serif text-[26px] leading-tight font-normal">{opportunity.organization}</h4>
                  </div>
                  <div className="flex flex-wrap items-center gap-3">
                    {runId && <OpportunityFeedback organization={opportunity.organization} runId={runId} />}
                    <div className="flex items-baseline gap-1.5 rounded-full border px-3 py-1.5">
                      <strong className="text-[15px] font-semibold tabular-nums">{opportunity.priority}</strong>
                      <span className="text-[12px] text-muted-foreground">{scoreLabel(opportunity.priority, labels.score)}</span>
                    </div>
                  </div>
                </div>

                <div className="rounded-lg bg-muted/50 p-4">
                  <Eyebrow className="text-muted-foreground">{labels.clue}</Eyebrow>
                  <p className="m-0 mt-2 text-[13.5px] leading-relaxed">{opportunity.market_clue}</p>
                  <SourceAnchor
                    className="mt-3"
                    href={opportunity.source_link}
                    label={opportunity.source_name}
                    meta={opportunity.observed_on}
                    quote={opportunity.source_quote}
                  />
                </div>

                <div className="grid gap-4.5 sm:grid-cols-2">
                  <div>
                    <Eyebrow className="text-muted-foreground">{labels.relevance}</Eyebrow>
                    <p className="m-0 mt-2 text-[13px] leading-relaxed text-muted-foreground">{opportunity.relevance}</p>
                  </div>
                  <div>
                    <Eyebrow className="text-muted-foreground">{labels.momentum}</Eyebrow>
                    <p className="m-0 mt-2 text-[13px] leading-relaxed text-muted-foreground">{opportunity.momentum}</p>
                  </div>
                </div>

                <blockquote className="m-0 rounded-lg border border-primary/20 bg-secondary/60 p-4">
                  <Eyebrow className="text-primary">{labels.draft}</Eyebrow>
                  <p className="m-0 mt-2 mb-3 text-[13px] leading-relaxed whitespace-pre-line">{opportunity.message_draft}</p>
                  <Button
                    onClick={() => navigator.clipboard.writeText(opportunity.message_draft)}
                    size="sm"
                    type="button"
                    variant="outline"
                  >
                    <Copy size={14} /> {actions.copy}
                  </Button>
                </blockquote>

                <details className="group rounded-lg border p-4">
                  <summary className="flex cursor-pointer items-center justify-between text-[12px] font-semibold">
                    <span>{actions.sourceDetails}</span>
                    <ChevronDown className="transition-transform group-open:rotate-180" size={16} />
                  </summary>
                  <div className="mt-4 grid gap-4.5 sm:grid-cols-2">
                    <div>
                      <Eyebrow className="text-muted-foreground">{actions.observation}</Eyebrow>
                      <p className="m-0 my-2 text-[12.5px] leading-relaxed text-muted-foreground">{opportunity.source_summary}</p>
                      <SourceAnchor
                        href={opportunity.source_link}
                        label={opportunity.source_name}
                        meta={opportunity.observed_on}
                        quote={opportunity.source_quote}
                      />
                    </div>
                    <div>
                      <Eyebrow className="text-muted-foreground">{actions.contactRoute}</Eyebrow>
                      <p className="m-0 my-2 text-[12.5px] leading-relaxed text-muted-foreground">{opportunity.contact_route}</p>
                      <Badge className="text-[12px] whitespace-normal" variant="outline">
                        {labels.toVerify(opportunity.verification_note)}
                      </Badge>
                    </div>
                  </div>
                </details>
              </div>
            </Card>
          ))}
        </div>
      </div>

      <div className="mt-20 grid gap-4.5 lg:grid-cols-2 max-sm:mt-12">
        <Card className="p-7 max-sm:p-5">
          <Eyebrow>{labels.trends}</Eyebrow>
          <h3 className="mt-1 mb-3 font-serif text-[30px] leading-tight font-normal">{labels.trendsTitle}</h3>
          <div className="grid gap-4">
            {report.market_patterns.map((pattern) => (
              <div className="flex gap-4 border-t pt-4" key={pattern.label}>
                <strong className="font-serif text-[24px] leading-none font-normal text-primary">
                  {pattern.occurrences}×
                </strong>
                <div>
                  <h4 className="m-0 mb-1 text-[13.5px] font-semibold">{pattern.label}</h4>
                  <p className="m-0 text-[12.5px] leading-relaxed text-muted-foreground">{pattern.interpretation}</p>
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card className="bg-accent p-7 max-sm:p-5">
          <Eyebrow>{labels.nextSequence}</Eyebrow>
          <h3 className="mt-1 mb-3 font-serif text-[30px] leading-tight font-normal">{report.action_plan.positioning}</h3>
          <ol className="m-0 grid list-none gap-3.5 p-0">
            {[report.action_plan.launch, report.action_plan.follow_through, report.action_plan.target_outcome].map(
              (step, index) => (
                <li className="flex gap-3" key={index}>
                  <span className="grid size-6 flex-none place-items-center rounded-full bg-primary text-[12px] font-semibold text-primary-foreground">
                    {index + 1}
                  </span>
                  <p className="m-0 text-[13px] leading-relaxed">{step}</p>
                </li>
              ),
            )}
          </ol>
        </Card>
      </div>

      <Card className="mt-4.5 flex-row items-start gap-4 p-6">
        <ShieldCheck className="mt-0.5 size-5 shrink-0 text-muted-foreground" />
        <div>
          <strong className="text-[13px] font-semibold">{labels.caveats}</strong>
          <ul className="mt-2 mb-0 grid gap-1.5 pl-4 text-[12.5px] leading-relaxed text-muted-foreground">
            {report.caveats.map((caveat) => <li key={caveat}>{caveat}</li>)}
          </ul>
        </div>
      </Card>
    </section>
  );
}
